<script lang="ts">
    import { AskPositionMessage_AskType, AvailabilityStatus } from "@workadventure/messages";
    import * as Sentry from "@sentry/svelte";
    import highlightWords from "highlight-words";
    import { defaultColor } from "@workadventure/shared-utils";
    import { localUserStore } from "../../../Connection/LocalUserStore";
    import { availabilityStatusStore } from "../../../Stores/MediaStore";
    import { getColorHexOfStatus } from "../../../Utils/AvailabilityStatus";
    import type { ChatUser } from "../../Connection/ChatConnection";
    import { LL } from "../../../../i18n/i18n-svelte";
    import { chatSearchBarValue } from "../../Stores/ChatStore";
    import { resolveChatUserColor } from "../../Connection/Matrix/services/WaMatrixProfileService";
    import { getMatrixClientForChatTint, openDirectChatRoom } from "../../Utils";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import {
        PHYSICAL_PRESENCE_ABSENT_SINCE_VARIABLE,
        PHYSICAL_PRESENCE_PUBLIC_VARIABLE,
        PHYSICAL_PRESENCE_AGENT_VARIABLE,
        physicalPresenceActivationStore,
        physicalPresenceAbsentSinceStore,
        physicalPresenceAgentAvailableStore,
        type PhysicalPresenceActivationState,
    } from "../../../Stores/PhysicalPresenceStore";
    import { analyticsClient } from "../../../Administration/AnalyticsClient";
    import { createFloatingUiActions } from "../../../Utils/svelte-floatingui";
    import Avatar from "../Avatar.svelte";
    import UserActionButton from "./UserActionButton.svelte";
    import { IconLoader, IconSend } from "@wa-icons";

    interface Props {
        user: ChatUser;
        isMatrixChatEnabled: boolean;
    }

    let { user, isMatrixChatEnabled = true }: Props = $props();

    let showRoomCreationInProgress = false;

    let { chatId, availabilityStatus, username = "", color, isAdmin, pictureStore } = $derived(user);

    /** Tint: local name, Matrix `account_data`, or peer cache — deps keep the row in sync. */
    let resolvedAvatarColor = $derived(
        chatId !== undefined && chatId !== ""
            ? (resolveChatUserColor(chatId, color, getMatrixClientForChatTint()) ?? defaultColor)
            : defaultColor,
    );

    let isMe = $derived(
        user.chatId === localUserStore.getChatId() || user.uuid === localUserStore.getLocalUser()?.uuid,
    );

    let userStatus = $derived(isMe ? availabilityStatusStore : availabilityStatus);

    let canViewPresenceMonitoring = $state(false);
    let presenceMonitoringState = $state<PhysicalPresenceActivationState | "unknown">("unknown");
    let presenceAbsentSince = $state<number | null>(null);
    let presenceAgentAvailable = $state<boolean | null>(null);
    let presenceCounterNow = $state(Date.now());

    // Mercadologica: remote computers can have a clock ahead of this one.
    // Keep a local anchor for a future remote absentSince so the counter
    // starts immediately instead of remaining at 0s until clocks catch up.
    let remoteAbsentSinceRaw: number | null = null;
    let remoteAbsentSinceNormalized: number | null = null;

    function isPresenceActivationState(value: unknown): value is PhysicalPresenceActivationState {
        return value === "enabled" || value === "disabled" || value === "error";
    }

    function getPresenceMonitoringLabel(state: PhysicalPresenceActivationState | "unknown"): string {
        switch (state) {
            case "enabled":
                return "Presença automática ativa";
            case "disabled":
                return "Presença automática não ativada";
            case "error":
                return "Erro na presença automática";
            default:
                return "Presença automática sem informação";
        }
    }

    function formatAbsentDuration(absentSince: number): string {
        const totalSeconds = Math.max(0, Math.floor((presenceCounterNow - absentSince) / 1_000));
        if (totalSeconds < 60) {
            return `${totalSeconds}s`;
        }
        const totalMinutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        if (totalMinutes < 60) {
            return `${totalMinutes}min ${seconds}s`;
        }
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes}min`;
    }

    function refreshPresenceMonitoring(): void {
        presenceCounterNow = Date.now();
        try {
            const currentScene = gameManager.getCurrentGameScene();
            const presenceViewerTags = currentScene.connection?.getAllTags() ?? [];
            canViewPresenceMonitoring =
                presenceViewerTags.includes("supervisor-presenca") || presenceViewerTags.includes("admin");

            if (!canViewPresenceMonitoring) {
                presenceMonitoringState = "unknown";
                presenceAbsentSince = null;
                presenceAgentAvailable = null;
                remoteAbsentSinceRaw = null;
                remoteAbsentSinceNormalized = null;
                return;
            }

            if (isMe) {
                remoteAbsentSinceRaw = null;
                remoteAbsentSinceNormalized = null;
                presenceMonitoringState = $physicalPresenceActivationStore;
                presenceAbsentSince = $physicalPresenceAbsentSinceStore;
                presenceAgentAvailable = $physicalPresenceAgentAvailableStore;
                return;
            }

            const remotePlayer = user.uuid
                ? currentScene.getRemotePlayersRepository().getPlayerByUuid(user.uuid)
                : undefined;
            const value = remotePlayer?.variables.get(PHYSICAL_PRESENCE_PUBLIC_VARIABLE);
            presenceMonitoringState = isPresenceActivationState(value) ? value : "unknown";
            const absentSince = remotePlayer?.variables.get(PHYSICAL_PRESENCE_ABSENT_SINCE_VARIABLE);

            if (typeof absentSince === "number" && Number.isFinite(absentSince)) {
                if (absentSince !== remoteAbsentSinceRaw) {
                    remoteAbsentSinceRaw = absentSince;

                    // Ignore small clock differences. If the remote clock is
                    // more than 5 seconds ahead, anchor this absence locally once.
                    remoteAbsentSinceNormalized =
                        absentSince > Date.now() + 5_000 ? Date.now() : absentSince;
                }

                presenceAbsentSince = remoteAbsentSinceNormalized;
            } else {
                remoteAbsentSinceRaw = null;
                remoteAbsentSinceNormalized = null;
                presenceAbsentSince = null;
            }

            const agentAvailable = remotePlayer?.variables.get(PHYSICAL_PRESENCE_AGENT_VARIABLE);
            presenceAgentAvailable = typeof agentAvailable === "boolean" ? agentAvailable : null;
        } catch {
            canViewPresenceMonitoring = false;
            presenceMonitoringState = "unknown";
            presenceAbsentSince = null;
            presenceAgentAvailable = null;
            remoteAbsentSinceRaw = null;
            remoteAbsentSinceNormalized = null;
        }
    }

    $effect(() => {
        refreshPresenceMonitoring();
        const timer = window.setInterval(refreshPresenceMonitoring, 1_000);
        return () => window.clearInterval(timer);
    });

    let sendButtonTooltipVisible = $state(false);
    const [sendButtonFloatingRef, sendButtonFloatingContent, sendButtonArrowAction] = createFloatingUiActions(
        { placement: "top" },
        8,
    );

    let chunks = $derived(
        highlightWords({
            text: username.match(/\[\d*]/) ? username.substring(0, username.search(/\[\d*]/)) : username,
            query: $chatSearchBarValue,
        }),
    );

    const roomCreationInProgress = gameManager.chatConnection.roomCreationInProgress;

    function getNameOfAvailabilityStatus(status: AvailabilityStatus) {
        switch (status) {
            case AvailabilityStatus.ONLINE:
                return $LL.chat.status.online();
            case AvailabilityStatus.AWAY:
                return $LL.chat.status.away();
            case AvailabilityStatus.BUSY:
                return $LL.chat.status.busy();
            case AvailabilityStatus.DO_NOT_DISTURB:
                return $LL.chat.status.do_not_disturb();
            case AvailabilityStatus.BACK_IN_A_MOMENT:
                return $LL.chat.status.back_in_a_moment();
            case AvailabilityStatus.SOUND_BLOCKED:
                return $LL.chat.status.sound_blocked();
            case AvailabilityStatus.JITSI:
            case AvailabilityStatus.BBB:
            case AvailabilityStatus.LIVEKIT:
                return $LL.chat.status.meeting();
            case AvailabilityStatus.SPEAKER:
                return $LL.chat.status.megaphone();
            case AvailabilityStatus.SILENT:
            default:
                return $LL.chat.status.unavailable();
        }
    }

    let loadingDirectRoomAccess = false;

    function openWokaMenu() {
        if (user.uuid == undefined) return;
        // Track the open woka menu action
        analyticsClient.openWokaMenu();

        const currentScene = gameManager.getCurrentGameScene();

        const remotePlayerData = currentScene.getRemotePlayersRepository().getPlayerByUuid(user.uuid);
        if (remotePlayerData != undefined) {
            const remotePlayer = currentScene.MapPlayersByKey.get(remotePlayerData.userId);
            if (remotePlayer != undefined) {
                remotePlayer.activate();
                return;
            }
        }

        currentScene.connection?.emitAskPosition(
            user.uuid ?? "",
            user.playUri ?? "",
            AskPositionMessage_AskType.LOCATE,
        );
    }
</script>

{#if loadingDirectRoomAccess}
    <div class="min-h-[60px] text-md flex gap-2 justify-center flex-row items-center p-1">
        <IconLoader class="animate-spin" />
    </div>
{:else}
    <div class="flex flex-col px-2 pb-2 user">
        <div
            class="wa-chat-item {isAdmin
                ? 'admin'
                : 'user'} group/chatItem relative mb-[1px] text-md flex gap-2 flex-row items-center transition-all hover:bg-white/10 hover:rounded hover:!cursor-pointer px-2 py-2 cursor-pointer"
        >
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
                class="relative shrink-0 wa-avatar {!$userStatus ? 'opacity-50' : ''} cursor-pointer"
                onclick={(event) => {
                    event.stopPropagation();
                    openWokaMenu();
                }}
            >
                <Avatar compact {pictureStore} fallbackName={username || "?"} color={resolvedAvatarColor} />
            </div>
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
                class={`flex-auto ms-1 ${!$userStatus && "opacity-50"} cursor-pointer`}
                onclick={(event) => {
                    event.stopPropagation();
                    openWokaMenu();
                }}
            >
                <div class="flex items-center h-4">
                    <div class="text-sm font-bold mb-0 flex items-center text-nowrap">
                        {#each chunks as chunk (chunk.key)}
                            <div class={`${chunk.match ? "text-light-blue" : ""}`}>
                                {chunk.text}
                            </div>
                        {/each}
                        {#if username && username.match(/\[\d*]/)}
                            <div class="font-light text-xs text-gray">
                                #{username
                                    .match(/\[\d*]/)
                                    ?.join()
                                    ?.replace("[", "")
                                    ?.replace("]", "")}
                            </div>
                        {/if}
                        {#if isAdmin}
                            <div
                                class="text-xxs bg-secondary rounded-sm px-1 py-0.5 ms-1"
                                title={$LL.chat.role.admin()}
                            >
                                {$LL.chat.role.adminShort()}
                            </div>
                        {/if}
                    </div>
                </div>
                <div class="text-xs mb-0 font-condensed opacity-75 self-end">
                    {#if isMe}
                        {$LL.chat.you()}
                        {#if $userStatus === AvailabilityStatus.AWAY && canViewPresenceMonitoring && presenceAbsentSince !== null}
                            · Ausente há {formatAbsentDuration(presenceAbsentSince)}
                        {/if}
                    {:else if $userStatus}
                        <div class="flex items-center brightness-150" style="color:{getColorHexOfStatus($userStatus)}">
                            <div
                                class="rounded-full me-1 h-1.5 w-1.5"
                                style="background:{getColorHexOfStatus($userStatus)}"
                            ></div>
                            {getNameOfAvailabilityStatus($userStatus)}
                            {#if $userStatus === AvailabilityStatus.AWAY && canViewPresenceMonitoring && presenceAbsentSince !== null}
                                <span class="ms-1">há {formatAbsentDuration(presenceAbsentSince)}</span>
                            {/if}
                        </div>
                    {:else}
                        {$LL.chat.userList.disconnected()}
                    {/if}
                </div>
                {#if canViewPresenceMonitoring}
                    <div
                        class="text-xxs mt-0.5 font-condensed"
                        class:text-green-300={presenceMonitoringState === "enabled"}
                        class:text-amber-300={presenceMonitoringState === "disabled"}
                        class:text-red-300={presenceMonitoringState === "error"}
                        class:text-gray-400={presenceMonitoringState === "unknown"}
                        title={getPresenceMonitoringLabel(presenceMonitoringState)}
                    >
                        {presenceMonitoringState === "enabled"
                            ? "✓"
                            : presenceMonitoringState === "error"
                              ? "!"
                              : presenceMonitoringState === "disabled"
                                ? "⚠"
                                : "?"}
                        {getPresenceMonitoringLabel(presenceMonitoringState)}
                    </div>

                    <div
                        class="text-xxs mt-0.5 font-condensed"
                        class:text-green-300={presenceAgentAvailable === true}
                        class:text-amber-300={presenceAgentAvailable === false}
                        class:text-gray-400={presenceAgentAvailable === null}
                        title="Status do Mercadológica Presence Agent neste computador"
                    >
                        {presenceAgentAvailable === true
                            ? "✓ Agente Windows ativo"
                            : presenceAgentAvailable === false
                              ? "⚠ Agente Windows não detectado"
                              : "? Agente Windows sem informação"}
                    </div>
                {/if}
            </div>
            <div class="flex flex-wrap justify-start items-start content-start">
                <div class="transition-all">
                    {#if !isMe && $userStatus != 0}
                        <UserActionButton {user} />
                    {/if}
                </div>
                {#if !isMe && !showRoomCreationInProgress && isMatrixChatEnabled}
                    <!-- svelte-ignore a11y_no_static_element_interactions -->
                    <div
                        class="relative"
                        use:sendButtonFloatingRef
                        onmouseenter={() => (sendButtonTooltipVisible = true)}
                        onmouseleave={() => (sendButtonTooltipVisible = false)}
                    >
                        <button
                            class="transition-all hover:bg-white/10 p-2 rounded-md aspect-square flex items-center justify-center m-0"
                            class:text-white={user.chatId !== undefined}
                            class:text-gray-400={user.chatId === undefined}
                            data-testId={`send-message-${user.username}`}
                            disabled={user.chatId === undefined}
                            onclick={(event) => {
                                event.stopPropagation();
                                openDirectChatRoom(chatId).catch((error) => {
                                    console.error("Error opening direct chat room:", error);
                                    Sentry.captureException(error, {
                                        extra: {
                                            userId: user.uuid,
                                            chatId: chatId,
                                            playUri: user.playUri,
                                            username: user.username,
                                        },
                                    });
                                });
                                analyticsClient.sendMessageFromUserList();
                            }}
                        >
                            <IconSend font-size="16" />
                        </button>
                        {#if sendButtonTooltipVisible}
                            <div
                                use:sendButtonFloatingContent
                                class="send-button-tooltip absolute z-50 bg-contrast/90 backdrop-blur-xl text-white text-nowrap p-2 rounded text-sm"
                            >
                                <div class="!top-[30%] !-translate-x-1/2" use:sendButtonArrowAction></div>
                                {#if user.chatId === undefined}
                                    {$LL.chat.remoteUserNotConnected()}
                                {:else}
                                    {$LL.chat.userList.sendMessage()}
                                {/if}
                            </div>
                        {/if}
                    </div>
                {:else if $roomCreationInProgress && showRoomCreationInProgress}
                    <div class="min-h-[30px] text-md flex gap-2 justify-center flex-row items-center p-1">
                        <IconLoader class="animate-spin" />
                    </div>
                {/if}
            </div>
        </div>
    </div>

    <style>
        .status {
            background-color: var(--color);
        }

        .send-button-tooltip {
            opacity: 0;
            transition: opacity 0.2s ease-in-out;
            animation: sendButtonTooltipFadeIn 0.2s forwards;
        }

        @keyframes sendButtonTooltipFadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }
    </style>
{/if}
